
import { useContentManager } from '../../hooks/useContentManager';
import { useEffect, useState } from 'react';

export default function HeroSection() {
  const { getContentItem, loading } = useContentManager();
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoKey, setVideoKey] = useState<number>(0);
  const [hasError, setHasError] = useState(false);
  // Admin-managed hero text
  const heroTextItem = getContentItem('home', 'hero-text');
  const heroTitleLine1 = heroTextItem?.metadata?.title_line1 as string | undefined;
  const heroTitleBold = heroTextItem?.metadata?.title_bold as string | undefined;
  const heroSubtitle = heroTextItem?.metadata?.subtitle as string | undefined;
  const heroButtonText = heroTextItem?.metadata?.button_text as string | undefined;
  const heroButtonUrl = heroTextItem?.metadata?.button_url as string | undefined;
  
  // Default video URL
  const defaultVideoUrl = '';
  
  useEffect(() => {
    if (!loading) {
      const heroVideo = getContentItem('home', 'hero-video');
      
      // Get video URL - prefer video_url, then content field
      let url = '';
      if (heroVideo?.video_url && heroVideo.video_url.trim() !== '') {
        url = heroVideo.video_url;
      } else if (heroVideo?.content && heroVideo.content.trim() !== '') {
        url = heroVideo.content;
      } else {
        // Varsayılan video
        url = defaultVideoUrl;
      }
      
      // URL değiştiyse video'yu yenile
      if (url !== videoUrl) {
        setVideoUrl(url);
        setVideoKey(prev => prev + 1);
        setHasError(false);
      }
      
      setVideoLoading(false);
    }
  }, [loading, getContentItem, videoUrl]);

  const handleVideoError = () => {
    if (videoUrl !== defaultVideoUrl && !hasError) {
      setHasError(true);
      setVideoUrl(defaultVideoUrl);
      setVideoKey(prev => prev + 1);
    }
  };

  if (loading || videoLoading) {
    return (
      <section className="relative h-screen w-full overflow-hidden bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading video...</div>
      </section>
    );
  }

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        {videoUrl ? (
          <video
            key={`hero-video-${videoKey}-${videoUrl}`}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onError={handleVideoError}
            onLoadStart={() => {}}
            onCanPlay={() => {}}
            onLoadedData={() => {}}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="w-full h-full bg-gray-900 flex items-center justify-center">
            <div className="text-white text-xl">Video not found</div>
          </div>
        )}
        <div className="absolute inset-0 bg-black/20"></div>
      </div>
      
      {/* Hero İçerik Alanı */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-4">
          {hasError && (
            <div className="mb-4 px-4 py-2 bg-red-600/80 rounded-lg">
              <p className="text-sm">Custom video failed to load, showing default video</p>
            </div>
          )}
          {(heroTitleLine1 || heroTitleBold || heroSubtitle || (heroButtonText && heroButtonText.trim())) && (
            <>
              <h1 className="text-5xl lg:text-7xl font-light tracking-wide text-white mb-4">
                {heroTitleLine1 || ''} {heroTitleBold && (<span className="font-bold">{heroTitleBold}</span>)}
              </h1>
              {heroSubtitle?.trim() && (
                <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 mb-6">
                  {heroSubtitle}
                </p>
              )}
              {heroButtonText?.trim() && (
                heroButtonUrl && heroButtonUrl.trim() !== '#' ? (
                  <a href={heroButtonUrl} className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap">
                    {heroButtonText.trim()}
                  </a>
                ) : (
                  <button className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap">
                    {heroButtonText.trim()}
                  </button>
                )
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
